import React, { useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = React.createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [csrfToken, setCsrfToken] = useState(null);
  const [darkMode, setDarkMode] = useState(
    localStorage.getItem('darkMode') === null ? true : localStorage.getItem('darkMode') === 'true'
  );

  // Configure axios to send cookies with all requests
  useEffect(() => {
    axios.defaults.withCredentials = true;

    // Add response interceptor to handle auth errors
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // If we get a 401 (unauthorized) or token expired, clear the session
        if (error.response?.status === 401 || error.response?.data?.expired) {
          sessionStorage.removeItem('user');
          setUser(null);
          setCsrfToken(null);
          // Redirect to login if not already there
          if (window.location.pathname !== '/login' && window.location.pathname !== '/the-librarian') {
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );

    // Try to restore user session from sessionStorage on mount
    try {
      const userData = JSON.parse(sessionStorage.getItem('user') || 'null');
      if (userData) {
        setUser(userData);
      }
    } catch (error) {
      console.error('Failed to parse user data from sessionStorage:', error);
      sessionStorage.removeItem('user');
    }

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  // Set CSRF token in axios headers when it changes
  useEffect(() => {
    if (csrfToken) {
      axios.defaults.headers.common['X-CSRF-Token'] = csrfToken;
    } else {
      delete axios.defaults.headers.common['X-CSRF-Token'];
    }
  }, [csrfToken]);

  useEffect(() => {
    document.body.className = darkMode ? 'dark-mode' : '';
    localStorage.setItem('darkMode', darkMode);
  }, [darkMode]);

  const login = (user, csrf) => {
    // Store user data in sessionStorage (not sensitive, just for UI)
    sessionStorage.setItem('user', JSON.stringify(user));
    setUser(user);
    setCsrfToken(csrf);
  };

  const logout = async () => {
    try {
      // Call logout endpoint to clear httpOnly cookies
      await axios.post('/api/logout');
    } catch (error) {
      console.error('Logout request failed:', error);
    } finally {
      // Clear local state regardless of server response
      sessionStorage.removeItem('user');
      setUser(null);
      setCsrfToken(null);
    }
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  return (
    <AuthContext.Provider value={{ user, csrfToken, login, logout, darkMode, toggleDarkMode }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
